import { useEffect, useRef, useState } from "react";
import type { ChainBlock, VerifyResult } from "../../../shared/types";
import { fmtShort, shortHash } from "../../format";

const LinkIcon = () => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

export function ChainRail({ chain, verify, isVet, onOpen, onVerify, onTamper }: {
  chain: ChainBlock[];
  verify: VerifyResult | null;
  isVet: boolean;
  onOpen: (b: ChainBlock) => void;
  onVerify: () => void;
  onTamper: () => void;
}) {
  const [open, setOpen] = useState(false); // collapsed by default — the record gets the full width
  const [showNotes, setShowNotes] = useState(false);
  const body = useRef<HTMLDivElement>(null);
  const broken = new Set(verify?.blocks.filter((b) => !(b.linkOk && b.hashOk && b.payloadOk)).map((b) => b.seq));
  const notes = chain.filter((b) => b.type === "note").length;
  const shown = chain.filter((b) => showNotes || b.type !== "note" || broken.has(b.seq));
  const last = shown[shown.length - 1]?.seq;

  // Newest block is at the bottom (time flows down) — keep it in view.
  useEffect(() => {
    if (body.current) {
      body.current.scrollTop = body.current.scrollHeight;
      body.current.scrollLeft = body.current.scrollWidth;
    }
  }, [chain.length, showNotes, open]);

  if (!open) {
    const ok = verify?.valid;
    return (
      <aside className="chain-rail collapsed">
        <button className="rail-tab" onClick={() => setOpen(true)} title="Show the record chain">
          <span className={`rail-state${verify && !ok ? " bad" : ""}`}>{!verify ? "…" : ok ? <LinkIcon /> : "⚠"}</span>
          <span className="rail-label">Record chain</span>
          <span className="rail-count">{chain.length}</span>
          <span className="rail-arrow">›</span>
        </button>
      </aside>
    );
  }

  return (
    <aside className="chain-rail">
      <div className="chain-card">
        <div className="chain-head">
          <button className="chain-title as-btn" onClick={() => setOpen(false)} title="Collapse">‹ Record chain</button>
          {!verify ? (
            <div className="chain-badge">Verifying…</div>
          ) : verify.valid ? (
            <div className="chain-badge" title={`All ${verify.blocks.length} blocks re-verified ${new Date(verify.checkedAt).toLocaleTimeString()}`}><LinkIcon />Anchored</div>
          ) : (
            <div className="chain-badge bad">⚠ Broken</div>
          )}
        </div>
        <div className="chain-body" ref={body}>
          <div className="chain-track" />
          {shown.map((b) => {
            const cls = ["chain-block", b.seq === last && "current", b.type === "note" && "note", broken.has(b.seq) && "broken"].filter(Boolean).join(" ");
            return (
              <button key={b.seq} className={cls} onClick={() => onOpen(b)} title={`Block #${b.seq}\nhash ${b.hash}\nprev ${b.prevHash}\nsealed by ${b.author}${b.clinic ? ` · ${b.clinic}` : ""}`}>
                <div className="chain-dot" />
                <div className="chain-date">
                  {fmtShort(b.date)}
                  <span className="chain-idx">{b.seq === last ? `block #${b.seq} · current` : `block #${b.seq}`}</span>
                </div>
                <div className="chain-card-inner">
                  <div className="chain-note">{b.summary}</div>
                  {b.type !== "note" && <div className="chain-hash">{b.type === "registration" ? "genesis · " : ""}{shortHash(b.hash)}</div>}
                </div>
              </button>
            );
          })}
          <div className="chain-time">↓ TIME</div>
        </div>
        {notes > 0 && (
          <button className="chain-toggle" onClick={() => setShowNotes((s) => !s)}>
            {showNotes ? "Hide" : "Show"} {notes} behavior-note block{notes === 1 ? "" : "s"}
          </button>
        )}
        <div className="chain-actions">
          <button className="btn ghost sm" onClick={onVerify}>Re-verify chain</button>
          {isVet && <button className="btn ghost sm" onClick={onTamper}>Try to alter a record</button>}
        </div>
        <div className="chain-foot">Each visit is a hash-linked block in the pet's record — tamper-evident, chronological, always in order.</div>
      </div>
    </aside>
  );
}
